/**
 * Section Avatar Service
 * 
 * Service for managing section avatars/orchestrators, specifically for projects
 */

import api from './api';

/**
 * Get all sections with avatars and orchestrators in a single API call
 * This replaces multiple individual calls to improve performance
 * 
 * @param {string} projectId - The project ID
 * @param {boolean} active - Filter by active sections (optional)
 * @returns {Promise<Object>} Consolidated section data
 */
export const getConsolidatedSectionData = async (projectId, active = true) => {
  try {
    if (!projectId) {
      return {
        success: false,
        message: 'Project ID is required',
        data: []
      };
    }

    console.log(`Fetching consolidated section data for project ${projectId}`);
    
    const nocacheParam = `_nocache=${Date.now()}`;
    const activeParam = active ? `&active=${active}` : '';
    
    const response = await api.get(
      `/api/sections/project/${projectId}/consolidated?${nocacheParam}${activeParam}`
    );
    
    console.log('Consolidated section data response:', response.data);
    
    if (response.data && response.data.success) {
      return {
        success: true,
        data: response.data.data,
        count: response.data.count,
        metadata: response.data.metadata,
        message: 'Successfully retrieved consolidated section data'
      };
    }
    
    return {
      success: false,
      message: 'Failed to retrieve consolidated section data',
      data: []
    };
    
  } catch (error) {
    console.error('Error fetching consolidated section data:', error);
    
    // Fallback to individual calls if consolidated endpoint fails
    console.log('Falling back to individual API calls...');
    return {
      success: false,
      message: error.message || 'Error fetching consolidated section data',
      data: [],
      fallback: true
    };
  }
};

/**
 * Get section avatar for a specific project
 * 
 * @param {string} sectionId - The section ID
 * @param {string} projectId - The project ID
 * @returns {Promise<Object>} Avatar data including url and name
 */
export const getSectionAvatar = async (sectionId, projectId) => {
  try {
    if (!sectionId || !projectId) {
      return {
        success: false,
        message: 'Section ID and Project ID are required'
      };
    }

    // No special handling for newly created projects - they should have empty avatars initially
    // Don't use session storage to track project state
    console.log(`Always fetch from API for project ${projectId}, section ${sectionId}`);
    // Return empty values if API call fails

    // Log every fetch for debugging
    console.log(`SectionAvatarService: Fetching avatar for section=${sectionId}, project=${projectId}`);

    // No cache management - always fetch from API
    console.log(`Always fetching fresh data from API for section=${sectionId}, project=${projectId}`);

    // First attempt: try the project-specific orchestrator endpoint
    // NOTE: This may fail due to permissions or if not implemented in the backend
    try {
      console.log(`Trying project-specific orchestrator endpoint for section=${sectionId}, project=${projectId}`);
      // Use a timestamp query parameter instead of cache-control headers to avoid CORS issues
      const nocacheParam = `_nocache=${Date.now()}`;
      const orchestratorResponse = await api.get(
        `/api/sections/project/${projectId}/section/${sectionId}/orchestrator?${nocacheParam}`
      );
      
      console.log('Response from orchestrator endpoint:', orchestratorResponse.data);
      
      // If we get a successful response with an orchestrator, use it
      // Backend returns: { success, data: { orchestrator: { name, avatar_url, ... } } }
      const orchData = orchestratorResponse.data?.data?.orchestrator || orchestratorResponse.data?.orchestrator;
      if (orchestratorResponse.data && orchestratorResponse.data.success &&
          orchData && (orchData.avatar_url || orchData.avatarUrl)) {

        // Don't cache the result, always fetch fresh
        console.log('Successfully retrieved orchestrator data from API');

        return {
          success: true,
          avatarUrl: orchData.avatar_url || orchData.avatarUrl,
          avatarName: orchData.name || orchData.orchestratorName || orchData.orchestrator_name,
          message: 'Retrieved from orchestrator API'
        };
      }
    } catch (err) {
      console.log('Orchestrator endpoint failed, falling back to basic avatar endpoint:', err.message);
    }
    
    // Second attempt: fallback to the basic avatar endpoint with timestamp param instead of cache headers
    console.log(`Fetching avatar using basic endpoint for section=${sectionId}, project=${projectId}`);
    // Use timestamp parameter instead of cache-control headers to avoid CORS issues
    const nocacheParam = Date.now();
    const basicResponse = await api.get(
      `/api/sections/${sectionId}/avatar?projectId=${projectId}&sectionId=${sectionId}&_nocache=${nocacheParam}`
    );
    
    console.log('Response from basic avatar endpoint:', basicResponse.data);
    
    // Process the response from the basic avatar endpoint
    // Backend returns snake_case: avatar_url, avatar_name
    if (basicResponse.data && basicResponse.data.success) {
      // Check if we have avatar data (handle both snake_case and camelCase)
      const avatarUrl = basicResponse.data.avatar_url || basicResponse.data.avatarUrl;
      const avatarName = basicResponse.data.avatar_name || basicResponse.data.avatarName;
      if (avatarUrl) {
        console.log(`Found avatar for section=${sectionId}, project=${projectId}:`, basicResponse.data);

        // Don't cache the result, always fetch fresh
        console.log('Successfully retrieved avatar data from basic API endpoint');

        return {
          success: true,
          avatarUrl: avatarUrl,
          avatarName: avatarName,
          message: 'Retrieved from basic avatar API'
        };
      }
    }
    
    // No valid avatar found or API failed, return empty state without caching
    console.log(`No valid avatar found for section=${sectionId}, project=${projectId}, returning empty state`);
    
    return {
      success: true,
      avatarUrl: null,
      avatarName: null,
      message: 'No valid avatar found, using default'
    };
  } catch (error) {
    console.error('Error fetching section avatar:', error);
    return {
      success: false,
      message: error.message || 'Error fetching avatar'
    };
  }
};

/**
 * Save section avatar for a specific project
 * 
 * @param {string} sectionId - The section ID
 * @param {string} projectId - The project ID
 * @param {Object} avatarData - Avatar data to save
 * @param {string} avatarData.avatarName - Name for the avatar
 * @param {string} avatarData.photoUrl - URL for the avatar image
 * @param {string} avatarData.type - Type of avatar (e.g., 'robot')
 * @returns {Promise<Object>} Result of the save operation
 */
export const saveSectionAvatar = async (sectionId, projectId, avatarData) => {
  try {
    if (!sectionId || !projectId) {
      return {
        success: false,
        message: 'Section ID and Project ID are required'
      };
    }

    // No session storage or cache being used

    // Non-caching error result in case the API fails
    const errorResult = {
      success: false,
      message: 'Failed to save avatar data to API'
    };

    // Create timeout promise for request
    const timeout = (ms) => {
      return new Promise((_, reject) => {
        setTimeout(() => {
          reject(new Error(`Request timed out after ${ms}ms`));
        }, ms);
      });
    };

    try {
      // DIRECT API CALL FIRST - most reliable
      // Prepare data structure for the basic API using snake_case to match backend expectations
      const basicData = {
        section_id: sectionId,
        avatar_name: avatarData.avatarName.trim(),
        photo_url: avatarData.photoUrl,
        type: avatarData.type || 'robot',
        project_id: projectId // Include projectId for uniqueness per project
      };

      console.log(`Saving avatar directly using avatar endpoint for section=${sectionId}, project=${projectId}`, basicData);

      // Set timeout only (no cache-control headers to avoid CORS issues)
      const options = {
        timeout: 5000 // 5 second timeout
      };

      // Add timestamp parameter instead of using cache-control headers
      const nocacheParam = Date.now();

      // Race against timeout
      const basicResponse = await Promise.race([
        api.post(`/api/sections/${sectionId}/avatar/select?projectId=${projectId}&_nocache=${nocacheParam}`, basicData, options),
        timeout(5000)
      ]);

      console.log('Response from avatar save endpoint:', basicResponse.data);

      if (basicResponse.data && basicResponse.data.success) {
        // Don't save to local cache
        console.log('Successfully saved avatar data to API');

        return {
          success: true,
          avatarUrl: basicResponse.data.avatar_url || basicResponse.data.avatarUrl || avatarData.photoUrl,
          avatarName: basicResponse.data.avatar_name || basicResponse.data.avatarName || avatarData.avatarName.trim(),
          message: 'Avatar saved successfully'
        };
      }

      // If we got here, the basic endpoint failed but didn't throw an error
      console.log('Basic avatar endpoint returned unsuccessful response');
      return errorResult;
    } catch (err) {
      console.log('Error with basic avatar endpoint:', err.message);

      // If the error message mentions CORS, add additional logging
      if (err.message.includes('CORS') || err.message.includes('Cross-Origin')) {
        console.warn('CORS error detected. This may be due to missing headers in the server response.');
        // Store an error flag in sessionStorage to help with debugging
        sessionStorage.setItem('cors_error_detected', 'true');
      }

      try {
        // Fallback to the orchestrator endpoint
        console.log('Falling back to orchestrator endpoint');

        // Prepare data for project-specific orchestrator using snake_case to match backend expectations
        const orchestratorData = {
          orchestrator_name: avatarData.avatarName.trim(),
          orchestrator_role: avatarData.type || 'robot',
          avatar_url: avatarData.photoUrl,
          project_id: projectId // Include projectId for uniqueness per project
        };

        // Set timeout only (no cache-control headers to avoid CORS issues)
        const options = {
          timeout: 5000 // 5 second timeout
        };

        // Add timestamp parameter instead of using cache-control headers
        const nocacheParam = Date.now();

        // Race against timeout
        const orchestratorResponse = await Promise.race([
          api.post(`/api/sections/project/${projectId}/section/${sectionId}/orchestrator?_nocache=${nocacheParam}`, orchestratorData, options),
          timeout(5000)
        ]);

        console.log('Response from orchestrator save endpoint:', orchestratorResponse.data);

        if (orchestratorResponse.data && orchestratorResponse.data.success) {
          // Don't save to local cache
          console.log('Successfully saved avatar data to orchestrator API');

          // Backend returns: { success, data: { orchestrator: { name, avatar_url, ... } } }
          const orchSaveData = orchestratorResponse.data?.data?.orchestrator || orchestratorResponse.data?.orchestrator;
          return {
            success: true,
            avatarUrl: orchSaveData?.avatar_url || orchSaveData?.avatarUrl || avatarData.photoUrl,
            avatarName: orchSaveData?.name || orchSaveData?.orchestratorName || avatarData.avatarName.trim(),
            message: 'Orchestrator saved successfully'
          };
        }
      } catch (innerErr) {
        console.log('Error with orchestrator endpoint too:', innerErr.message);
      }

      // Both endpoints failed, return error
      console.log('All API attempts failed, returning error');
      return errorResult;
    }
  } catch (error) {
    console.error('Fatal error saving section avatar:', error);

    // Always return a usable response instead of throwing
    return {
      success: false,
      avatarUrl: avatarData.photoUrl,
      avatarName: avatarData.avatarName.trim(),
      message: 'Error saving avatar: ' + (error.message || 'Unknown error')
    };
  }
};

/**
 * We no longer use session storage for avatars, so this is a no-op
 *
 * @param {string} projectId - The project ID
 */
export const clearProjectAvatars = (projectId) => {
  // Log operation for debugging
  console.log(`No avatar cache to clear for project: ${projectId} - we don't use cache anymore`);
  // No-op - we don't use session storage for avatars
};

/**
 * Debug function to list all avatar-related session storage entries
 * We no longer use session storage, so this is a no-op that returns an empty object
 *
 * @returns {Object} Empty object since we don't use session storage
 */
export const listAllAvatarStorage = () => {
  console.log('No avatar cache entries - we don\'t use cache anymore');
  return {};
};

export default {
  getConsolidatedSectionData,
  getSectionAvatar,
  saveSectionAvatar,
  clearProjectAvatars,
  listAllAvatarStorage
};